/**
 * The overview: is the platform healthy, and how is it being used. The page
 * an operator lands on after signing in.
 *
 * Health first, because a problem there is the one thing on this page that
 * needs doing now. Each check says what it means in words ("Newest backup is
 * 30 hours old"); the server judges, the page shows. It is what the API
 * process that answered can see — its database, its Redis, its clock jobs,
 * the backups folder if it is mounted there.
 *
 * Usage is totals across every restaurant: counts, orders and restaurants
 * trading per day, the busiest this week and the quiet ones worth a call.
 * Takings are one chart per currency, never one figure: restaurants trade in
 * their own money, and nothing here converts it.
 *
 * Both refresh every minute while the tab is in view.
 */

import { Link } from 'react-router-dom';
import {
  displayLocale,
  formatMoney,
  usePlatformHealth,
  usePlatformUsage,
  type PlatformHealth,
  type PlatformUsage,
} from '@restaurant/api-client';
import { Badge, Bars, Card, Empty, ErrorNotice, Loading } from '@restaurant/ui';

import { healthHeadline, plural, shortDay, statusTone, statusWords } from '../overview-words';
import { formatDay, formatMoment } from '../words';
import './overview.css';

export function OverviewPage() {
  const health = usePlatformHealth();
  const usage = usePlatformUsage();
  const locale = displayLocale();

  return (
    <>
      <header className="pf__head">
        <div>
          <h1 className="pf__title">Overview</h1>
          <p className="pf__sub">
            Whether the platform is working, and how it is being used. Refreshes every minute.
          </p>
        </div>
        {health.data && (
          <p className="pf__faint">Checked {formatMoment(health.data.checked_at, locale)}</p>
        )}
      </header>

      {usage.data && <Figures usage={usage.data} />}

      <Card title="Health">
        {health.isPending && <Loading label="Checking" />}
        {health.isError && (
          <ErrorNotice title="Could not check the platform" message={health.error.message} />
        )}
        {health.data && <HealthList health={health.data} />}
      </Card>

      {usage.isPending && <Loading label="Counting" />}
      {usage.isError && (
        <ErrorNotice title="Could not count the usage" message={usage.error.message} />
      )}
      {usage.data && <Usage usage={usage.data} locale={locale} />}
    </>
  );
}

function HealthList({ health }: { health: PlatformHealth }) {
  const problems = health.checks.filter((c) => c.status === 'problem').length;
  const warnings = health.checks.filter((c) => c.status === 'warning').length;
  // What needs a look is listed; what is fine is counted, and one tap away.
  // Fourteen green rows pushed the numbers an operator came for off the page.
  const attention = health.checks.filter((c) => c.status !== 'ok');
  const fine = health.checks.filter((c) => c.status === 'ok');
  return (
    <>
      <p className="pf__ov-headline">
        <Badge tone={statusTone(health.status)}>{statusWords(health.status)}</Badge>{' '}
        {healthHeadline(health.status, problems, warnings)}
      </p>
      {attention.length > 0 && <Checks checks={attention} />}
      {fine.length > 0 && (
        <details className="pf__ov-fine">
          <summary>
            {plural(fine.length, 'check', 'checks')} fine
            <span className="pf__faint">
              {' '}
              —{' '}
              {fine
                .map((c) => c.label)
                .slice(0, 4)
                .join(', ')}
              {fine.length > 4 ? '…' : ''}
            </span>
          </summary>
          <Checks checks={fine} />
        </details>
      )}
    </>
  );
}

function Checks({ checks }: { checks: PlatformHealth['checks'] }) {
  return (
    <ul className="pf__ov-checks">
      {checks.map((check) => (
        <li key={check.key} className="pf__ov-check">
          <span className="pf__ov-badge">
            <Badge tone={statusTone(check.status)}>{statusWords(check.status)}</Badge>
          </span>
          <span className="pf__ov-label">{check.label}</span>
          <span className="pf__ov-reason">
            {check.reason}
            {check.detail && <span className="pf__ov-detail">{check.detail}</span>}
          </span>
        </li>
      ))}
    </ul>
  );
}

function Figures({ usage }: { usage: PlatformUsage }) {
  const counts = usage.restaurants;
  return (
    <dl className="pf__ov-figures">
      <Figure label="Restaurants" value={counts.total} lead />
      <Figure label="Active" value={counts.active} />
      <Figure label="On a free trial" value={counts.on_trial} />
      <Figure label="Suspended" value={counts.suspended} />
      <Figure label="Opened in 30 days" value={counts.opened_recently} />
      <Figure label="Sites open" value={counts.sites} />
    </dl>
  );
}

function Usage({ usage, locale }: { usage: PlatformUsage; locale: string }) {
  const period = `${formatDay(usage.since, locale)} to ${formatDay(usage.today, locale)}`;

  return (
    <>
      <div className="pf__grid">
        <Card title="Orders per day">
          <p className="pf__faint">Closed orders, every restaurant, {period}.</p>
          <Bars
            unit="day"
            fit
            bars={usage.days.map((d) => ({
              key: d.day,
              label: shortDay(d.day, locale),
              value: d.orders,
              caption: plural(d.orders, 'order', 'orders'),
            }))}
          />
        </Card>
        <Card title="Restaurants trading per day">
          <p className="pf__faint">Restaurants that closed at least one order that day.</p>
          <Bars
            unit="day"
            fit
            bars={usage.days.map((d) => ({
              key: d.day,
              label: shortDay(d.day, locale),
              value: d.restaurants,
              caption: plural(d.restaurants, 'restaurant', 'restaurants'),
            }))}
          />
        </Card>
      </div>

      <Card title="Takings">
        {usage.takings.length === 0 ? (
          <Empty title="No takings in the last 30 days" />
        ) : (
          <>
            <p className="pf__faint">
              One chart per currency. Restaurants trade in their own money, so these are never
              added together.
            </p>
            <div className="pf__grid">
              {usage.takings.map((series) => (
                <section key={series.currency} className="pf__ov-series">
                  <h3 className="pf__ov-series-title">
                    {series.currency}
                    <span className="pf__ov-series-total">
                      {formatMoney(series.total, series.currency, locale)} in 30 days
                    </span>
                  </h3>
                  <Bars
                    unit="day"
                    fit
                    bars={series.days.map((d) => ({
                      key: d.day,
                      label: shortDay(d.day, locale),
                      // Only sizes the bar; what is read is the caption.
                      value: Number(d.amount),
                      caption: formatMoney(d.amount, series.currency, locale),
                    }))}
                  />
                </section>
              ))}
            </div>
          </>
        )}
      </Card>

      <div className="pf__grid">
        <Card title="Busiest this week">
          {usage.busiest_this_week.length === 0 ? (
            <Empty title="No orders in the last seven days" />
          ) : (
            <ol className="pf__ov-ranked">
              {usage.busiest_this_week.map((r) => (
                <li key={r.id}>
                  <Link className="pf__rowlink" to={`/restaurants/${r.id}`}>
                    {r.name}
                  </Link>
                  <span className="pf__ov-count">{plural(r.orders, 'order', 'orders')}</span>
                </li>
              ))}
            </ol>
          )}
        </Card>
        <Card title={`Quiet for ${usage.quiet_days} days`}>
          {usage.quiet.length === 0 ? (
            <Empty title="Every restaurant has traded lately" />
          ) : (
            <>
              <p className="pf__faint">
                Active restaurants with no order in {usage.quiet_days} days. Worth a call.
              </p>
              <ul className="pf__ov-ranked">
                {usage.quiet.map((r) => (
                  <li key={r.id}>
                    <Link className="pf__rowlink" to={`/restaurants/${r.id}`}>
                      {r.name}
                    </Link>
                    <span className="pf__ov-count">
                      {r.last_order_on
                        ? `Last order ${formatDay(r.last_order_on, locale)}`
                        : `No orders since it opened, ${formatMoment(r.opened_at, locale)}`}
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </Card>
      </div>
    </>
  );
}

function Figure({
  label,
  value,
  lead = false,
}: {
  label: string;
  value: number;
  lead?: boolean;
}) {
  return (
    <div className={`pf__ov-figure${lead ? ' is-lead' : ''}`}>
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}
